<template>
  <div>
    <v-dialog
      v-model="isPopupOpen"
      max-width="600"
      persistent
    >
      <v-card>
        <v-container>
          <v-alert
            v-model="errorAlert"
            type="error"
            outlined
            dismissible
            transition="scale-transition"
          >
            <v-flex
              v-for="error in errors.all()"
              :key="error"
            >
              <span>
                {{ error }}
              </span>
            </v-flex>
          </v-alert>
          <v-flex
            xs12
            style="margin-inline: auto"
          >
            <v-file-input
              v-model="archiveFile"
              accept="application/zip"
              placeholder="Upload your ZIP"
            ></v-file-input>
          </v-flex>
          <v-flex xs12>
            <tree-select
              v-model="categoryId"
              :multiple="false"
              :options="categories"
              :append-to-body="true"
              :default-expand-level="1"
              :disable-branch-nodes="true"
              :placeholder="$t('documents.categories.selectPlaceholder')"
              :searchable="false"
              :class="$style.treeselect"
            >
              <div
                slot="option-label"
                slot-scope="{ node }"
              >
                {{ node.raw.name }}
              </div>
              <div
                slot="value-label"
                slot-scope="{ node }"
              >
                {{ transformLabel(node) }}
              </div>
            </tree-select>
          </v-flex>
          <v-card-actions>
            <v-btn
              color="blue darken-1"
              text
              @click.native="close"
            >
              {{ $t('documents.buttons.cancel') }}
            </v-btn>
            <v-btn
              :loading="saving"
              :disabled="saving"
              color="primary"
              text
              @click.native="uploadFile"
            >
              {{ $t('documents.buttons.save') }}
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showFailedDialog"
      max-width="600"
    >
      <v-card>
        <v-card-title class="headline">
          Some files were not uploaded:
        </v-card-title>

        <v-card-text :class="$style['scrollable-list']">
          <v-list :class="$style.miniList">
            <v-list-item
              v-for="(file, i) in failedFiles"
              :key="i"
              :class="$style.miniItem"
            >
              <v-list-item-title
                :class="[$style.miniTitle, 'text-truncate']"
              >
                {{ file }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="primary"
            @click="showFailedDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import errorAlertMixins from '@/mixins/errorAlertMixins';
import TreeSelect from '@riophae/vue-treeselect';
import documents from '@/api/documents';
import { ADMIN_LOAD_DOCUMENTS } from '@/store/Documents/actions';
import { mapActions } from 'vuex';

export default {
  components: {
    TreeSelect,
  },
  mixins: [errorAlertMixins],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      archiveFile: null,
      categoryId: null,
      saving: false,
      showFailedDialog: false,
      failedFiles: null,
    };
  },
  methods: {
    ...mapActions({
      loadAdminDocuments: `documents/${ADMIN_LOAD_DOCUMENTS}`,
    }),
    /**
     * Transform category label into the appropriate view.
     *
     * @param {Object} node - Category to transform.
     *
     * @return {Object}
     */
    transformLabel(node) {
      let label = null;

      if (node.parentNode) {
        label = this.transformLabel(node.parentNode);
      }

      return label ? `${label} / ${node.raw.name}` : node.raw.name;
    },
    close() {
      this.hideError();
      this.isPopupOpen = false;
      this.archiveFile = null;
      this.categoryId = null;
      this.errors.clear();
    },

    async uploadFile() {
      this.errors.clear();
      if (!this.archiveFile) {
        this.errors.add({
          field: 'archiveFile',
          msg: this.$t('documents.errors.file'),
        });
      }
      if (!this.categoryId) {
        this.errors.add({
          field: 'categoryId',
          msg: this.$t('documents.errors.categoryId'),
        });
      }
      await this.$validator.validateAll();
      if (this.errors.count()) {
        this.errorAlert = true;
        return;
      }
      this.saving = true;

      try {
        const {
          success = false,
          files: { failed = [] },
        } = await documents.zipUpload(
          {
            archiveFile: this.archiveFile,
            categoryId: this.categoryId,
          },
        );

        if (success) {
          await this.loadAdminDocuments();
          this.archiveFile = null;
          this.categoryId = null;
        }

        if (failed.length) {
          this.failedFiles = failed;
          this.showFailedDialog = true;
        }

        this.isPopupOpen = false;
      } catch (e) {
        this.parseErrorResponse(e.response);
        this.hideError();
      } finally {
        this.saving = false;
      }
    },
  },
  computed: {
    isPopupOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },
};
</script>
<style lang="scss" module>
.treeselect {
  margin-block: 24px;
}
.scrollable-list {
  max-height: 50dvh;
  overflow-y: auto;
}

.miniList {
  padding: 0;
}

.miniItem {
  padding: 0 0 !important;
  min-height: unset !important;
}

.miniTitle {
  font-size: 13px;
  line-height: 1.2;
  padding: 2px 0;
}
</style>
